import { useState, useEffect, useMemo, useRef } from 'react'
import { getFirestoreUtils } from '@/lib/firebase'
import { useAuthStore } from '@/stores/authStore'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import type { SharedTask, UnifiedTask } from '@/lib/sharedTypes'
import { normalizeAssignees } from '@/lib/sharedTypes'

/**
 * 모든 공유 그룹의 할 일을 실시간 구독
 * -> 메인 TaskList, Calendar, Profile에서 통합 표시용
 */
export function useMySharedTasks(): UnifiedTask[] {
  const uid = useAuthStore((s) => s.user?.uid)
  const sharedGroups = useSharedGroupStore((s) => s.sharedGroups)
  const [myTasks, setMyTasks] = useState<UnifiedTask[]>([])

  // 안정적 의존성: 그룹 ID가 실제로 변경될 때만 effect 재실행
  // (메타데이터 변경 시 배열 참조가 바뀌어도 구독을 재생성하지 않음)
  const groupIdsKey = useMemo(
    () => sharedGroups.map((g) => g.id).sort().join(','),
    [sharedGroups],
  )

  // 최신 그룹 메타데이터(name, color)를 콜백 안에서 참조하기 위한 ref
  const sharedGroupsRef = useRef(sharedGroups)
  sharedGroupsRef.current = sharedGroups

  useEffect(() => {
    if (!uid || !groupIdsKey) {
      setMyTasks([])
      return
    }

    let cancelled = false
    const unsubs: (() => void)[] = []
    const groups = sharedGroupsRef.current

    // 떠나거나 삭제된 그룹의 할 일을 즉시 제거 (다른 그룹 스냅샷을 기다리지 않음)
    // 구독 대상이 아닌 그룹의 잔여 task가 통합 목록에 남는 문제 방지
    const subscribedGroupIds = new Set(groups.map((g) => g.id))
    setMyTasks((prev) =>
      prev.filter((t) => t.groupId != null && subscribedGroupIds.has(t.groupId)),
    )

    async function subscribe() {
      const { firestore, collection, onSnapshot } = await getFirestoreUtils()

      // async gap 이후 취소 여부 확인 (cleanup이 먼저 실행된 경우)
      if (cancelled) return

      // 각 공유 그룹의 모든 tasks 구독
      for (const group of groups) {
        if (cancelled) break
        const tasksCol = collection(firestore, `sharedGroups/${group.id}/tasks`)

        const unsub = onSnapshot(
          tasksCol,
          (snapshot) => {
            if (cancelled) return

            const currentGroup = sharedGroupsRef.current.find((g) => g.id === group.id)
            const tasks: UnifiedTask[] = snapshot.docs
              .map((doc) => {
                const data = doc.data() as SharedTask
                const { assigneeNames } = normalizeAssignees(data)
                return {
                  source: 'shared' as const,
                  sharedTask: { ...data, id: doc.id },
                  groupId: group.id,
                  groupName: currentGroup?.name ?? group.name,
                  groupColor: currentGroup?.color ?? group.color,
                  id: `s-${group.id}-${doc.id}`,
                  title: data.title,
                  status: data.status,
                  priority: data.priority,
                  dueDate: data.dueDate,
                  dueTime: data.dueTime,
                  assigneeName: assigneeNames.join(', ') || undefined,
                  assigneeNames: assigneeNames.length > 0 ? assigneeNames : undefined,
                }
              })
              // 내가 배정된 할일만 포함 (미배정은 제외)
              .filter((t) => {
                const { assigneeIds } = normalizeAssignees(t.sharedTask!)
                return assigneeIds.includes(uid!)
              })

            // 이 그룹의 할 일만 교체
            setMyTasks((prev) => {
              const otherGroups = prev.filter((t) => t.groupId !== group.id)
              return [...otherGroups, ...tasks]
            })
          },
          (error) => {
            console.error(`SharedTasks onSnapshot error (group ${group.id}):`, error)
          },
        )

        unsubs.push(unsub)
      }
    }

    subscribe()

    return () => {
      cancelled = true
      unsubs.forEach((u) => u())
    }
  }, [uid, groupIdsKey])

  return myTasks
}
